"use client";

import * as React from "react";
import { MinusIcon } from "lucide-react";

import { cn } from "@/lib/utils";

type SlotState = {
  char: string;
  hasFakeCaret: boolean;
  isActive: boolean;
};

type InputOTPContextValue = {
  slots: SlotState[];
  focusInput: () => void;
};

const InputOTPContext = React.createContext<InputOTPContextValue | null>(null);

type InputOTPProps = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "value" | "defaultValue" | "onChange" | "children" | "maxLength"
> & {
  value?: string;
  defaultValue?: string;
  maxLength?: number;
  onChange?: (value: string) => void;
  containerClassName?: string;
  children: React.ReactNode;
};

const DEFAULT_MAX_LENGTH = 6;

const InputOTP = React.forwardRef<HTMLInputElement, InputOTPProps>(
  (
    {
      value: valueProp,
      defaultValue = "",
      maxLength = DEFAULT_MAX_LENGTH,
      onChange,
      containerClassName,
      className,
      children,
      autoFocus,
      onFocus,
      onBlur,
      onKeyDown,
      onPaste,
      ...inputProps
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const [internalValue, setInternalValue] = React.useState(
      (valueProp ?? defaultValue ?? "").slice(0, maxLength),
    );
    const [isFocused, setIsFocused] = React.useState(false);

    const isControlled = valueProp !== undefined;
    const value = (isControlled ? valueProp : internalValue) ?? "";
    const sanitizedValue = value.slice(0, maxLength);

    React.useEffect(() => {
      if (autoFocus) {
        const frame = requestAnimationFrame(() => {
          inputRef.current?.focus();
        });
        return () => cancelAnimationFrame(frame);
      }
      return undefined;
    }, [autoFocus]);

    React.useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const commitValue = (nextValue: string) => {
      const truncated = nextValue.slice(0, maxLength);
      if (!isControlled) {
        setInternalValue(truncated);
      }
      onChange?.(truncated);
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const next = event.target.value.replace(/\s+/g, "");
      commitValue(next);
    };

    const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
      onPaste?.(event);
      if (event.defaultPrevented) {
        return;
      }

      const pasted = event.clipboardData.getData("text").replace(/\s+/g, "");
      if (!pasted) {
        return;
      }

      event.preventDefault();
      commitValue(pasted);
    };

    const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      onFocus?.(event);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      onBlur?.(event);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === "Backspace" && !sanitizedValue) {
        event.preventDefault();
      }
      onKeyDown?.(event);
    };

    const focusInput = () => inputRef.current?.focus();

    const activeIndex = Math.min(
      sanitizedValue.length === 0 ? 0 : sanitizedValue.length - 1,
      maxLength - 1,
    );
    const showCaretIndex =
      sanitizedValue.length < maxLength ? sanitizedValue.length : -1;

    const slots: SlotState[] = React.useMemo(
      () =>
        Array.from({ length: maxLength }).map((_, index) => ({
          char: sanitizedValue[index] ?? "",
          hasFakeCaret: isFocused && index === showCaretIndex,
          isActive: isFocused && index === activeIndex,
        })),
      [activeIndex, isFocused, maxLength, sanitizedValue, showCaretIndex],
    );

    const { inputMode, ...restInputProps } = inputProps;

    return (
      <InputOTPContext.Provider value={{ slots, focusInput }}>
        <div
          className={cn(
            "relative flex flex-col items-stretch",
            containerClassName,
          )}
          onClick={focusInput}
        >
          <input
            ref={inputRef}
            value={sanitizedValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            autoFocus={autoFocus}
            maxLength={maxLength}
            className="sr-only"
            inputMode={inputMode ?? "numeric"}
            {...restInputProps}
          />
          <div className={cn("flex items-center gap-2", className)}>
            {children}
          </div>
        </div>
      </InputOTPContext.Provider>
    );
  },
);

InputOTP.displayName = "InputOTP";

function InputOTPGroup({
  className,
  ...props
}: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="input-otp-group"
      className={cn("flex items-center", className)}
      {...props}
    />
  );
}

function InputOTPSlot({
  index,
  className,
  ...props
}: React.ComponentProps<"div"> & {
  index: number;
}) {
  const context = React.useContext(InputOTPContext);
  const slot = context?.slots[index] ?? {
    char: "",
    hasFakeCaret: false,
    isActive: false,
  };

  return (
    <div
      data-slot="input-otp-slot"
      data-active={slot.isActive}
      className={cn(
        "relative flex h-11 w-11 items-center justify-center rounded-xl border border-[#e4e7ef] bg-white text-base font-semibold text-[#111827] shadow-[0_18px_32px_-28px_rgba(15,23,42,0.65)] transition-all data-[active=true]:border-[#4EAFFF] data-[active=true]:ring-2 data-[active=true]:ring-[#4EAFFF]/25",
        className,
      )}
      onClick={context?.focusInput}
      {...props}
    >
      {slot.char}
      {slot.hasFakeCaret && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="animate-caret-blink h-4 w-px rounded-full bg-[#4EAFFF] duration-1000" />
        </div>
      )}
    </div>
  );
}

function InputOTPSeparator(props: React.ComponentProps<"div">) {
  return (
    <div data-slot="input-otp-separator" role="separator" {...props}>
      <MinusIcon />
    </div>
  );
}

export { InputOTP, InputOTPContext, InputOTPGroup, InputOTPSlot, InputOTPSeparator };
